/**
 * Appointment — public appointment booking flow.
 *
 * Self-contained scheduler: GET /api/availability lists open ~1-hour slots
 * (server is the source of truth; bookings open at least 48h out). The client
 * enters their contact details + the home address Andy will visit, then picks
 * a day (calendar) and time, and POSTs to /api/appointments.
 */

import React, { useEffect, useRef, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { DayPicker } from 'react-day-picker';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

import AddressFields from '../components/AddressFields';
import Turnstile, { turnstileEnabled } from '../components/Turnstile';
import { pushEvent } from '../lib/analytics';
import api from '../lib/api';

import 'react-day-picker/style.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Exact wording shown to the user on the consent checkbox (Step 1). Sent to
// the server alongside consent=true so each lead / appointment row has the
// wording in its audit trail. Kept identical to the Quote form's wording.
//
// SC insurance-compliance / legal review complete (approved 2026-07-16) — this
// wording is cleared for launch. Mirrors the WordPress Contact Form 7 consent
// language (iprotectyou.family, captured 2026-05-29) so the disclosure is
// identical across the marketing site and the app.
const CONSENT_TEXT =
  'By submitting this form, I agree that I Protect You LLC ' +
  '(iProtectYou.Family) and a licensed insurance agent may contact me at the ' +
  'phone number and email address I provided — by phone call, text message ' +
  '(SMS), and email — regarding insurance products and services. I understand ' +
  'these calls and texts may be made using automated dialing technology or a ' +
  'prerecorded or artificial voice, and that consent is not a condition of ' +
  'purchasing any product or service. Message frequency may vary, and message ' +
  'and data rates may apply. Reply STOP to unsubscribe from texts or HELP for ' +
  'help. See our Privacy Policy and Terms of Service.';

const emptyAddress = {
  line1: '',
  line2: '',
  city: '',
  state: '',
  postal_code: '',
  formatted: '',
  lat: null,
  lon: null,
};

const dayLong = (dateStr) => format(parseISO(dateStr), 'EEEE, MMMM d, yyyy');
const dayKey = (date) => format(date, 'yyyy-MM-dd');

export default function Appointment() {
  const [days, setDays] = useState([]);
  const [loadStatus, setLoadStatus] = useState('loading'); // loading|ready|error
  const [selectedDate, setSelectedDate] = useState('');
  const [slot, setSlot] = useState(null); // { start, end, label }

  const [step, setStep] = useState('contact'); // contact|schedule|done
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [age, setAge] = useState('');
  const [sex, setSex] = useState(''); // '' | 'male' | 'female'
  const [smoker, setSmoker] = useState(''); // '' | 'yes' | 'no'
  const [address, setAddress] = useState(emptyAddress);
  const [notes, setNotes] = useState('');
  const [consent, setConsent] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const leadCaptured = useRef(false);

  // Anti-spam: honeypot field (humans never see it), time-to-submit, and the
  // Turnstile token for the step-1 warm-lead capture (the widget renders only
  // when the site key is configured).
  const [website, setWebsite] = useState('');
  const [tsToken, setTsToken] = useState('');
  const tsRef = useRef(null);
  const formStartRef = useRef(Date.now());

  const loadAvailability = async () => {
    setLoadStatus('loading');
    try {
      const res = await api.get('/api/availability');
      setDays(res.data.days || []);
      setLoadStatus('ready');
    } catch {
      setLoadStatus('error');
    }
  };

  useEffect(() => {
    loadAvailability();
  }, []);

  const slotsForSelected =
    days.find((d) => d.date === selectedDate)?.slots || [];

  // Calendar inputs derived from availability: which days are bookable, and
  // the navigable month range (first → last open day).
  const availableDates = new Set(days.map((d) => d.date));
  const sortedDates = days.map((d) => d.date).sort();
  const firstAvail = sortedDates[0] ? parseISO(sortedDates[0]) : undefined;
  const lastAvail = sortedDates.length
    ? parseISO(sortedDates[sortedDates.length - 1])
    : undefined;

  const cityStateZip = [
    address.city,
    [address.state, address.postal_code].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ');
  const addressSummary = [address.line1, address.line2, cityStateZip]
    .filter(Boolean)
    .join(', ');

  const pickDay = (date) => {
    setSelectedDate(date);
    setSlot(null);
  };

  // Capture the contact as a warm lead the moment step 1 is completed, so a
  // visitor who drops off before choosing a time is still a follow-up lead.
  // Best-effort, once per session, and never blocks the flow.
  const captureLead = () => {
    if (leadCaptured.current) return;
    leadCaptured.current = true;
    pushEvent('generate_lead', {
      lead_type: 'booking_started',
    });
    api
      .post('/api/leads', {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        age: Number(age),
        sex,
        smoker: smoker === 'yes',
        address: {
          line1: address.line1.trim(),
          line2: address.line2.trim(),
          city: address.city.trim(),
          state: address.state.trim(),
          postal_code: address.postal_code.trim(),
        },
        notes: notes.trim() || undefined,
        consent: true,
        consent_text: CONSENT_TEXT,
        website,
        elapsed_ms: Date.now() - formStartRef.current,
        turnstile_token: tsToken || undefined,
      })
      .catch(() => {
        leadCaptured.current = false; // allow a retry on the next Continue
        // Turnstile tokens are single-use — mint a fresh one for the retry.
        tsRef.current?.reset();
      });
  };

  // Step 1 → Step 2: validate contact + address, capture the warm lead, then
  // move to scheduling.
  const goToSchedule = () => {
    setError(null);
    if (!name.trim() || !phone.trim() || !email.trim()) {
      setError('Please fill in your name, phone, and email.');
      return;
    }
    if (!EMAIL_RE.test(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    const phoneDigits = phone.replace(/\D/g, '');
    if (
      /[A-Za-z]/.test(phone) ||
      !(
        phoneDigits.length === 10 ||
        (phoneDigits.length === 11 && phoneDigits.startsWith('1'))
      )
    ) {
      setError('Please enter a valid 10-digit US phone number.');
      return;
    }
    const ageNum = Number(age);
    if (!age.trim() || !Number.isInteger(ageNum) || ageNum < 0 || ageNum > 120) {
      setError('Please enter a valid age (0–120).');
      return;
    }
    if (sex !== 'male' && sex !== 'female') {
      setError('Please select your sex.');
      return;
    }
    if (smoker !== 'yes' && smoker !== 'no') {
      setError('Please tell us whether you use tobacco or nicotine.');
      return;
    }
    if (
      !address.line1.trim() ||
      !address.city.trim() ||
      !address.state.trim() ||
      !address.postal_code.trim()
    ) {
      setError('Please complete your address (street, city, state, and ZIP).');
      return;
    }
    if (!consent) {
      setError('Please agree to the contact terms before continuing.');
      return;
    }
    // Only gate on Turnstile when a warm-lead capture will actually fire —
    // returning from "edit details" (already captured) needs no fresh token.
    if (turnstileEnabled() && !leadCaptured.current && !tsToken) {
      setError(
        'Please complete the verification above, or wait a moment for it to finish.',
      );
      return;
    }
    captureLead();
    setStep('schedule');
  };

  // Step 2: confirm the booking (contact + address already validated).
  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError(null);

    if (!slot) {
      setError('Please pick a day and time.');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/api/appointments', {
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        age: Number(age),
        sex,
        smoker: smoker === 'yes',
        start: slot.start,
        address: {
          line1: address.line1.trim(),
          line2: address.line2.trim(),
          city: address.city.trim(),
          state: address.state.trim(),
          postal_code: address.postal_code.trim(),
          formatted: address.formatted,
          lat: address.lat,
          lon: address.lon,
        },
        notes: notes.trim() || undefined,
        consent: true,
        consent_text: CONSENT_TEXT,
        website,
        elapsed_ms: Date.now() - formStartRef.current,
      });
      pushEvent('book_appointment', {
        lead_type: 'appointment_booked',
        appointment_start: slot.start,
      });
      setStep('done');
    } catch (err) {
      const code = err?.response?.status;
      const msg =
        err?.response?.data?.error ||
        'Something went wrong booking your appointment. Please try again or call Andy.';
      setError(msg);
      if (code === 409) {
        // Slot was taken between listing and submit — refresh and re-pick.
        setSlot(null);
        setStep('schedule');
        loadAvailability();
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Confirmation -------------------------------------------------------
  if (step === 'done') {
    return (
      <>
        <Helmet>
          <title>Appointment booked — iProtectYou.Family</title>
        </Helmet>
        <main style={wrap}>
          <h1 style={{ color: 'var(--color-navy)' }}>You're booked!</h1>
          <p style={{ lineHeight: 1.6 }}>
            Andy will meet you on{' '}
            <strong>{dayLong(selectedDate)}</strong> at{' '}
            <strong>{slot?.label} ET</strong>. He'll come to{' '}
            <strong>{addressSummary}</strong>.
          </p>
          <p style={{ lineHeight: 1.6 }}>
            We sent a confirmation to <strong>{email.trim()}</strong>. Need to
            change anything? Call or text Andy at{' '}
            <a href="tel:+18668792586">1-866-879-2586</a>.
          </p>
          <Link to="/" style={{ color: 'var(--color-royal)' }}>
            ← Back
          </Link>
        </main>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Book an Appointment — iProtectYou.Family</title>
      </Helmet>

      <main style={wrap}>
        <h1 style={{ margin: '0 0 0.75rem', color: 'var(--color-navy)' }}>
          Book an Appointment
        </h1>

        {/* Step 1: your details (name, phone, email, address) */}
        {step === 'contact' && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              goToSchedule();
            }}
            noValidate
          >
            <h2 style={h2}>1. Your details</h2>

            {/* Honeypot — hidden from humans (and screen readers); bots that
                auto-fill every field give themselves away. */}
            <div style={honeypotWrap} aria-hidden="true">
              <label htmlFor="b-website">Website</label>
              <input
                id="b-website"
                type="text"
                name="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            <label style={lbl} htmlFor="b-name">
              Full name
            </label>
            <input
              id="b-name"
              style={input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              disabled={submitting}
            />

            <label style={lbl} htmlFor="b-phone">
              Phone
            </label>
            <input
              id="b-phone"
              style={input}
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              autoComplete="tel"
              disabled={submitting}
            />

            <label style={lbl} htmlFor="b-email">
              Email
            </label>
            <input
              id="b-email"
              style={input}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              disabled={submitting}
            />

            <label style={lbl} htmlFor="b-age">
              Age
            </label>
            <input
              id="b-age"
              style={input}
              type="number"
              min="0"
              max="120"
              inputMode="numeric"
              value={age}
              onChange={(e) => setAge(e.target.value)}
              disabled={submitting}
            />

            <fieldset style={fieldset}>
              <legend style={legend}>Sex</legend>
              <label style={radioLbl}>
                <input
                  type="radio"
                  name="sex"
                  checked={sex === 'male'}
                  onChange={() => setSex('male')}
                  disabled={submitting}
                />{' '}
                Male
              </label>
              <label style={radioLbl}>
                <input
                  type="radio"
                  name="sex"
                  checked={sex === 'female'}
                  onChange={() => setSex('female')}
                  disabled={submitting}
                />{' '}
                Female
              </label>
            </fieldset>

            <fieldset style={fieldset}>
              <legend style={legend}>Do you use tobacco or nicotine?</legend>
              <label style={radioLbl}>
                <input
                  type="radio"
                  name="smoker"
                  checked={smoker === 'no'}
                  onChange={() => setSmoker('no')}
                  disabled={submitting}
                />{' '}
                No
              </label>
              <label style={radioLbl}>
                <input
                  type="radio"
                  name="smoker"
                  checked={smoker === 'yes'}
                  onChange={() => setSmoker('yes')}
                  disabled={submitting}
                />{' '}
                Yes
              </label>
            </fieldset>

            <fieldset style={fieldset}>
              <AddressFields
                value={address}
                onChange={setAddress}
                disabled={submitting}
              />
            </fieldset>

            <label style={lbl} htmlFor="b-notes">
              Anything Andy should know? <span style={optional}>(optional)</span>
            </label>
            <textarea
              id="b-notes"
              style={{ ...input, minHeight: 80, resize: 'vertical' }}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={submitting}
            />

            <label style={consentLbl}>
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                disabled={submitting}
                aria-required="true"
                data-testid="booking-consent"
              />
              <span>
                By submitting this form, I agree that I Protect You LLC
                (iProtectYou.Family) and a licensed insurance agent may contact
                me at the phone number and email address I provided — by phone
                call, text message (SMS), and email — regarding insurance
                products and services. I understand these calls and texts may be
                made using automated dialing technology or a prerecorded or
                artificial voice, and that consent is not a condition of
                purchasing any product or service. Message frequency may vary,
                and message and data rates may apply. Reply STOP to unsubscribe
                from texts or HELP for help. See our{' '}
                <a
                  href="https://iprotectyou.family/privacy-policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a
                  href="https://iprotectyou.family/terms-of-service/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Service
                </a>
                .
              </span>
            </label>

            <Turnstile ref={tsRef} onToken={setTsToken} />

            {error ? (
              <div role="alert" style={errorBox} data-testid="booking-error">
                {error}
              </div>
            ) : null}

            <button type="submit" style={submitBtn}>
              Continue →
            </button>
          </form>
        )}

        {/* Step 2: choose a day + time */}
        {step === 'schedule' && (
          <>
            <p style={{ ...muted, marginTop: 0 }}>
              Booking for{' '}
              <strong style={{ color: 'var(--color-navy)' }}>{name}</strong> ·{' '}
              <button
                type="button"
                style={linkBtn}
                onClick={() => setStep('contact')}
              >
                edit details
              </button>
            </p>

            {loadStatus === 'loading' && (
              <p style={muted}>Loading available times…</p>
            )}
            {loadStatus === 'error' && (
              <p style={{ ...muted, color: '#9F1239' }}>
                Couldn't load available times.{' '}
                <button style={linkBtn} onClick={loadAvailability}>
                  Try again
                </button>{' '}
                or call Andy at <a href="tel:+18668792586">1-866-879-2586</a>.
              </p>
            )}
            {loadStatus === 'ready' && days.length === 0 && (
              <p style={muted}>
                No open times right now. Please call or text Andy at{' '}
                <a href="tel:+18668792586">1-866-879-2586</a>.
              </p>
            )}

            {loadStatus === 'ready' && days.length > 0 && (
              <>
                <h2 style={h2}>2. Choose a day</h2>
                <div style={calendarWrap}>
                  <DayPicker
                    mode="single"
                    selected={selectedDate ? parseISO(selectedDate) : undefined}
                    onSelect={(date) => pickDay(date ? dayKey(date) : '')}
                    disabled={(date) => !availableDates.has(dayKey(date))}
                    startMonth={firstAvail}
                    endMonth={lastAvail}
                    defaultMonth={firstAvail}
                    showOutsideDays={false}
                  />
                </div>

                {selectedDate && (
                  <>
                    <h2 style={h2}>3. Choose a time</h2>
                    <div style={dayGrid}>
                      {slotsForSelected.map((s) => (
                        <button
                          key={s.start}
                          type="button"
                          onClick={() => setSlot(s)}
                          style={{
                            ...chip,
                            ...(slot?.start === s.start ? chipActive : {}),
                          }}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {error ? (
                  <div role="alert" style={errorBox} data-testid="booking-error">
                    {error}
                  </div>
                ) : null}

                {slot && (
                  <button
                    type="button"
                    style={submitBtn}
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting ? 'Booking…' : 'Confirm Appointment'}
                  </button>
                )}
              </>
            )}
          </>
        )}
      </main>
    </>
  );
}

const wrap = { maxWidth: 560, margin: '0 auto', padding: '2.5rem 1.5rem' };
// Off-screen (not display:none — some bots skip invisible fields).
const honeypotWrap = {
  position: 'absolute',
  left: '-9999px',
  top: 'auto',
  width: 1,
  height: 1,
  overflow: 'hidden',
};
const h2 = {
  fontSize: '1.05rem',
  color: 'var(--color-navy)',
  margin: '1.5rem 0 0.6rem',
};
const muted = { color: 'rgba(0,0,0,0.6)', fontSize: '0.95rem' };
const dayGrid = { display: 'flex', flexWrap: 'wrap', gap: 8 };
const calendarWrap = {
  display: 'inline-block',
  border: '1px solid rgba(0,0,0,0.12)',
  borderRadius: 12,
  padding: '0.5rem 0.75rem',
  // react-day-picker v9+ theming via CSS custom properties.
  '--rdp-accent-color': 'var(--color-navy)',
  '--rdp-accent-background-color': 'var(--color-navy)',
};
const chip = {
  padding: '0.55rem 0.85rem',
  fontSize: '0.92rem',
  background: '#fff',
  border: '1px solid rgba(0,0,0,0.18)',
  borderRadius: 999,
  cursor: 'pointer',
};
const chipActive = {
  background: 'var(--color-navy)',
  color: '#fff',
  borderColor: 'var(--color-navy)',
};
const lbl = { display: 'block', fontWeight: 600, margin: '1rem 0 0.35rem' };
const optional = { fontWeight: 400, color: 'rgba(0,0,0,0.45)' };
const input = {
  width: '100%',
  padding: '0.65rem 0.85rem',
  fontSize: '1rem',
  border: '1px solid rgba(0,0,0,0.15)',
  borderRadius: 8,
  boxSizing: 'border-box',
};
const fieldset = {
  border: '1px solid rgba(0,0,0,0.12)',
  borderRadius: 8,
  margin: '1.25rem 0 0',
  padding: '0.25rem 1rem 1rem',
};
const legend = { fontWeight: 600, padding: '0 0.35rem' };
const radioLbl = {
  display: 'inline-flex',
  alignItems: 'center',
  marginRight: '1.5rem',
};
const errorBox = {
  marginTop: '1rem',
  padding: '0.65rem 0.85rem',
  background: '#FEF2F2',
  color: '#9F1239',
  borderRadius: 8,
  fontSize: '0.92rem',
};
const linkBtn = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: 'var(--color-royal)',
  textDecoration: 'underline',
  cursor: 'pointer',
  font: 'inherit',
};
const submitBtn = {
  marginTop: '1.5rem',
  width: '100%',
  padding: '0.85rem 1rem',
  background: 'var(--color-navy)',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  fontSize: '1rem',
  fontWeight: 700,
  cursor: 'pointer',
};
const consentLbl = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  margin: '1.5rem 0 0',
  fontSize: '0.88rem',
  lineHeight: 1.45,
  color: 'rgba(0,0,0,0.7)',
};
